import React from 'react';

export type TextareaSize = 'sm' | 'md' | 'lg';

export interface TextareaProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> {
  label?: string;
  error?: string;
  helper?: string;
  size?: TextareaSize;
  maxLength?: number;
  showCharCount?: boolean;
}

const sizeStyles: Record<TextareaSize, string> = {
  sm: 'px-3 py-2 text-sm',
  md: 'px-4 py-3 text-base',
  lg: 'px-4 py-4 text-lg',
};

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      error,
      helper,
      size = 'md',
      disabled,
      maxLength,
      showCharCount = false,
      className,
      value,
      ...props
    },
    ref,
  ) => {
    const charCount = typeof value === 'string' ? value.length : 0;

    return (
      <div className="w-full">
        {label && (
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            {label}
            {props.required && <span className="text-error ml-1">*</span>}
          </label>
        )}

        <textarea
          ref={ref}
          disabled={disabled}
          maxLength={maxLength}
          value={value}
          className={`w-full rounded-lg border-2 font-sans transition-all duration-200 focus:outline-none resize-none ${error ? 'border-error bg-red-50 focus:ring-2 focus:ring-error focus:ring-offset-0' : 'border-neutral-300 bg-neutral-0 hover:border-neutral-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-100'} ${disabled ? 'bg-neutral-100 text-neutral-500 cursor-not-allowed border-neutral-200' : ''} ${sizeStyles[size]} ${className || ''}`}
          {...props}
        />

        <div className="mt-2 flex items-center justify-between">
          <div>
            {error && (
              <p className="text-sm font-medium text-error">{error}</p>
            )}
            {helper && !error && (
              <p className="text-sm text-neutral-600">{helper}</p>
            )}
          </div>

          {showCharCount && (
            <p className="text-xs text-neutral-500">
              {charCount}
              {maxLength ? `/${maxLength}` : ''}
            </p>
          )}
        </div>
      </div>
    );
  },
);

Textarea.displayName = 'Textarea';

export default Textarea;
